import type { EquityV6Compact } from './equityV6Format';

export type ElectricityProviderClass = 'IOU' | 'CCA' | 'POU' | 'unknown';

export function normalizeElectricityPriceSector(
  customerClassLabel: string | undefined
): 'Residential' | 'Commercial' | 'Industrial' {
  const s = (customerClassLabel ?? 'Residential').trim().toLowerCase();
  if (s.startsWith('com')) return 'Commercial';
  if (s.startsWith('ind')) return 'Industrial';
  return 'Residential';
}

/**
 * V6 EQ_ELECTRICITY_PRICE: core narrative is fixed; optional facts append only to the Data note.
 */
export function applyElectricityPriceEquityNote(
  compact: EquityV6Compact,
  context?: {
    energyBurdenSentence?: string | null;
    electricityVsGasSentence?: string | null;
    electricityOptionalBurdenSentence?: string | null;
    electricityOptionalGasCompareSentence?: string | null;
  }
): EquityV6Compact {
  const extras: string[] = [];
  const burden = (
    context?.electricityOptionalBurdenSentence ??
    context?.energyBurdenSentence
  )?.trim();
  const gasCmp = (
    context?.electricityOptionalGasCompareSentence ??
    context?.electricityVsGasSentence
  )?.trim();
  if (burden) extras.push(burden);
  if (gasCmp) extras.push(gasCmp);
  if (!extras.length) return compact;

  const baseNote = compact.note?.trim() ?? '';
  const note = baseNote ? `${baseNote} ${extras.join(' ')}`.trim() : extras.join(' ');
  return { ...compact, note };
}

/** Infer provider class for analytics (not used to rewrite V6 prose). */
export function inferElectricityProviderClass(params: {
  utilityName?: string | null;
  eia861Cca?: string | null;
  eia861Iou?: string | null;
}): ElectricityProviderClass {
  if (params.eia861Cca?.trim()) return 'CCA';
  const u = (params.utilityName ?? '').toLowerCase();
  if (
    /municipal|city of|ladwp|burbank water|sacramento municipal|riverside public|imperial irrigation|public utility district|pou\b/.test(
      u
    )
  ) {
    return 'POU';
  }
  if (
    /pacific gas|pg&e|southern california edison|\bsce\b|san diego gas|sdge/.test(u)
  ) {
    return 'IOU';
  }
  if (params.eia861Iou?.trim()) return 'IOU';
  return 'unknown';
}
